import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, CardActions,
  Button, Grid, Chip, LinearProgress, IconButton
} from '@mui/material';
import { Delete, ArrowForward, FavoriteBorder, Favorite } from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { wishlistAPI } from '../services/api';

const MotionCard = motion(Card);

const WishlistPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadWishlist(); }, []);

  const loadWishlist = async () => {
    setLoading(true);
    try {
      const res = await wishlistAPI.getAll();
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await wishlistAPI.remove(itemId);
      setItems(items.filter(i => i.item_id !== itemId));
      toast.success('Removed from wishlist');
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Favorite color="error" />
          <Typography variant="h4" fontWeight={700}>My Wishlist</Typography>
          <Chip label={`${items.length} items`} size="small" sx={{ ml: 1 }} />
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {items.length === 0 && !loading && (
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <FavoriteBorder sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>Your wishlist is empty</Typography>
            <Button variant="contained" component={Link} to="/marketplace" size="small">
              Browse Marketplace
            </Button>
          </Card>
        )}

        <Grid container spacing={3}>
          {items.map((item, i) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <MotionCard
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" fontWeight={600}>{item.item_name}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemove(item.item_id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Chip label={item.category} size="small" color="primary" variant="outlined" sx={{ mt: 1, mb: 2 }} />
                  <Typography variant="h5" color="primary.main" fontWeight={700}>
                    ₹{item.selling_price?.toLocaleString()}
                  </Typography>
                  <Chip
                    label={item.status}
                    size="small"
                    color={item.status === 'active' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
                <CardActions sx={{ px: 3, pb: 2 }}>
                  <Button
                    fullWidth variant="outlined" size="small"
                    component={Link} to={`/item/${item.item_id}`}
                    endIcon={<ArrowForward />}
                  >
                    View Item
                  </Button>
                </CardActions>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WishlistPage;

